import type { ColumnDef } from "@tanstack/react-table";

import type { BaseRowItem } from "../../components/data-table";
import type { AdminOtcTransaction } from "../../lib/api/adminTransactionsData";
import { formatAmount } from "../../utils/FormatPerson";

export type DeexDeskOtcTableRow = BaseRowItem & {
  raw: AdminOtcTransaction;
  displayDate: string;
  typeLabel: string;
  statusLabel: string;
  summary: string;
  userDisplay: string;
  userTitle: string;
};

function num(v: unknown): number | undefined {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function userFromTx(tx: AdminOtcTransaction): { display: string; title: string } {
  const u = tx.user;
  if (!u || typeof u !== "object") {
    const uid = str(tx.userId);
    return {
      display: uid.length > 14 ? `${uid.slice(0, 6)}…${uid.slice(-4)}` : uid || "—",
      title: uid || "—",
    };
  }
  const o = u as Record<string, unknown>;
  const email =
    o.email && typeof o.email === "object"
      ? str((o.email as Record<string, unknown>).address)
      : "";
  const profile =
    o.profile && typeof o.profile === "object"
      ? (o.profile as Record<string, unknown>)
      : null;
  const first = profile ? str(profile.firstName) : "";
  const last = profile ? str(profile.lastName) : "";
  const name = [first, last].filter(Boolean).join(" ").trim();
  const display = name || email || str(tx.userId) || "—";
  const title = [name, email].filter(Boolean).join(" · ") || str(tx.userId) || "—";
  return { display, title };
}

export function getOtcDisplayDate(tx: AdminOtcTransaction): string {
  const iso = str(tx.processedAt) || str(tx.createdAt);
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function formatOtcTypeLabel(typeRaw: unknown): string {
  const t = str(typeRaw).toLowerCase();
  if (t === "otc-deposit") return "OTC deposit";
  if (t === "otc-payout") return "OTC payout";
  if (!t) return "—";
  return t.replace(/-/g, " ");
}

export function formatOtcSummary(tx: AdminOtcTransaction): string {
  const t = str(tx.type).toLowerCase();
  const cur = str(tx.currency);
  const ngn = num(tx.amountNGN);
  const usd = num(tx.amountUSD);
  const payoutNgn = num(tx.payoutAmountNGN);

  if (t.includes("deposit")) {
    const parts: string[] = [];
    if (cur) parts.push(cur);
    if (ngn != null) parts.push(`₦${formatAmount(ngn)}`);
    if (usd != null) parts.push(`$${formatAmount(usd)}`);
    const addr = str(tx.txid);
    if (addr) parts.push(addr.length > 18 ? `${addr.slice(0, 10)}…` : addr);
    return parts.length ? parts.join(" · ") : "—";
  }

  if (t.includes("payout")) {
    const parts: string[] = [];
    if (payoutNgn != null) parts.push(`₦${formatAmount(payoutNgn)}`);
    const dep = str(tx.depositId);
    if (dep) parts.push(`deposit ${dep.length > 12 ? `${dep.slice(0, 6)}…` : dep}`);
    return parts.length ? parts.join(" · ") : "—";
  }

  if (ngn != null) return `₦${formatAmount(ngn)}`;
  return "—";
}

export function otcTypeBadgeClass(typeRaw: unknown): string {
  const t = str(typeRaw).toLowerCase();
  if (t.includes("deposit")) {
    return "bg-sky-500/15 text-sky-300 border-sky-500/35";
  }
  if (t.includes("payout")) {
    return "bg-emerald-500/15 text-emerald-300 border-emerald-500/35";
  }
  return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
}

export function otcStatusBadgeClass(statusRaw: unknown): string {
  const u = str(statusRaw).toUpperCase();
  if (u === "COMPLETED" || u === "SUCCESSFUL") {
    return "bg-emerald-500/15 text-emerald-400 border-emerald-500/35";
  }
  if (u === "CONFIRMED") {
    return "bg-cyan-500/15 text-cyan-300 border-cyan-500/35";
  }
  if (u === "FAILED") {
    return "bg-red-500/15 text-red-300 border-red-500/35";
  }
  if (u === "PENDING" || u === "HOLD" || u === "PROCESSING") {
    return "bg-amber-500/15 text-amber-300 border-amber-500/35";
  }
  return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
}

/**
 * Shape expected by {@link OtcDetailModal}: adds `kind` and optional `amountCrypto` alias.
 */
export function mapOtcTransactionForModal(
  tx: AdminOtcTransaction
): Record<string, unknown> & { kind: "DEPOSIT" | "PAYOUT" } {
  const typeStr = str(tx.type).toLowerCase();
  const kind: "DEPOSIT" | "PAYOUT" = typeStr.includes("payout")
    ? "PAYOUT"
    : "DEPOSIT";
  const out: Record<string, unknown> = { ...tx, kind };
  if (kind === "DEPOSIT") {
    const native = num(tx.amount);
    if (native != null && native > 0 && out.amountCrypto == null) {
      out.amountCrypto = native;
    }
  }
  return out as Record<string, unknown> & { kind: "DEPOSIT" | "PAYOUT" };
}

export function buildDeexDeskOtcTableRows(
  transactions: AdminOtcTransaction[]
): DeexDeskOtcTableRow[] {
  return transactions.map((tx) => {
    const { display, title } = userFromTx(tx);
    return {
      id: tx._id,
      raw: tx,
      displayDate: getOtcDisplayDate(tx),
      typeLabel: formatOtcTypeLabel(tx.type),
      statusLabel: str(tx.status) || "—",
      summary: formatOtcSummary(tx),
      userDisplay: display,
      userTitle: title,
    };
  });
}

export function createDeexDeskOtcTableColumns(
  onView: (row: DeexDeskOtcTableRow) => void
): ColumnDef<DeexDeskOtcTableRow, unknown>[] {
  return [
    {
      accessorKey: "displayDate",
      header: "Date",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-adminTable-muted">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "typeLabel",
      header: "Type",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${otcTypeBadgeClass(
            row.original.raw.type
          )}`}
        >
          {row.original.typeLabel}
        </span>
      ),
    },
    {
      accessorKey: "statusLabel",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${otcStatusBadgeClass(
            row.original.raw.status
          )}`}
        >
          {row.original.statusLabel}
        </span>
      ),
    },
    {
      accessorKey: "summary",
      header: "Amount / detail",
      meta: { wrap: true, maxWidth: "280px" },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-white">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "userDisplay",
      header: "User",
      meta: { hideBelow: "lg" as const },
      cell: ({ row }) => (
        <span
          className="text-adminTable-muted truncate max-w-[200px] inline-block"
          title={row.original.userTitle}
        >
          {row.original.userDisplay}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => (
        <button
          type="button"
          className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-secondary_button hover:border-secondary_button hover:bg-[#273037]"
          onClick={() => onView(row.original)}
        >
          View
        </button>
      ),
    },
  ];
}
